import React, { useState, useEffect, useCallback, useRef } from 'react';
import { C } from '../constants/theme';
import { Table } from '../components/Table';
import { Pagination } from '../components/Pagination';
import { PageHeader } from '../components/PageHeader';
import { SearchInput } from '../components/SearchInput';
import { Modal } from '../components/Modal';
import { FormField, inputStyle, selectStyle } from '../components/FormField';
import { Button } from '../components/Button';
import { Badge, BoolBadge, CategoryBadge } from '../components/Badge';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { LAYOUT_BOOL_FIELDS, ADS_TYPES, ADS_CATEGORIES, MORE_VIEWS, CONTENT_CATEGORIES, CONTENT_TYPE } from '../constants/options';
import { addOrUpdate, getLayout } from '../utils/LayoutService';
import { formatDate } from '../utils/dateUtils';
import apiClient from '../utils/apiClient';
import { getContent } from '../utils/ContentService';
import { getCategories } from '../utils/CategoryService';
import { ArrowUpDown, Pencil, Plus, Trash2, Upload, GripVertical } from 'lucide-react';
import { LayoutSequenceEditor } from '../model/LayoutPreviewModal';
import { getContentProvider } from '../utils/ContentProviderService';
import { uploadFile } from '../utils/Uploadservice';

// ─── Styles ───────────────────────────────────────────────────────────────────
const cardStyle = {
  background: C.white, borderRadius: 12, border: `1px solid ${C.border}`,
  boxShadow: '0 1px 4px rgba(0,0,0,.06)', overflow: 'hidden',
};

const sectionHeading = (label) => (
  <div style={{ gridColumn: 'span 2', borderTop: `1px solid ${C.border}`, paddingTop: 18, marginTop: 4 }}>
    <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: C.text }}>{label}</p>
  </div>
);

// ─── Init form ────────────────────────────────────────────────────────────────
const INIT_FORM = () => ({
  orderSequence: 1,
  containerType: 1,
  containerTypeDescription: null,
  title: null,
  endpointUrl: null,
  heroImageUrl: null,
  thumbnailUrl: null,
  autoScroll: false, isAds: false, ticker: false, icon: false, link: false,
  image: false, publisher: false, verticalScroll: false, horizontalScroll: false,
  verticalTabs: false, horizontalTabs: false, isMore: false, moreWebview: false, pipEnable: false,
  adsId: null,
  adsType: 'FullScreenVideoAds',
  adsCategory: 'googleAds',
  adsIndex: 0,
  status: 1,
  viewTypeId: 1,
  moreUrl: null,
  categoryId: null,
  contentCategory: null,
  contenType: 'ContentInfo',
  moreView: 'WEBVIEW',
  contentProviderId: '',
  enableIconButton: false,
  enableSuffle: false,
  iconButtonText: null,
  contentLimit: 10,
  payload: [],
});

// ─── Empty payload item ───────────────────────────────────────────────────────
const INIT_PAYLOAD_ITEM = () => ({
  orderSequence: 1,
  containerTypeDescription: '',
  title: '',
  redirectLink: '',
  heroImageUrl: '',
  thumbnailUrl: '',
  contentCategory: 'ARTICLE',
});

// ─── Media thumbnail ──────────────────────────────────────────────────────────
const MediaThumb = ({ url }) => {
  if (!url) return <span style={{ color: '#C4B5F4' }}>—</span>;
  const isVideo = url.toLowerCase().endsWith('.mp4');
  return isVideo ? (
    <video src={url} muted loop autoPlay playsInline
      style={{ width: 72, height: 48, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
  ) : (
    <img src={url} alt="thumb"
      style={{ width: 72, height: 48, objectFit: 'cover', borderRadius: 6, display: 'block' }}
      onError={(e) => { e.target.style.display = 'none'; }} />
  );
};

const CONTENT_COLUMNS = [
  { key: 'thumbnailUrl', label: 'Thumbnail', render: (v) => <MediaThumb url={v} /> },
  { key: 'title', label: 'Title' },
  { key: 'contentCategory', label: 'Category', render: (v) => <CategoryBadge label={v} /> },
  { key: 'status', label: 'Status', render: (v) => <Badge status={v} /> },
  { key: 'createdAt', label: 'Created At', render: (v) => formatDate(v) },
];

// ─── Reusable Multiselect Checkbox List ───────────────────────────────────────
const MultiCheckList = ({ items, value, onChange, labelKey = 'name', valueKey = 'id', renderLabel }) => {
  const selected = value ? String(value).split(',').map(s => s.trim()).filter(Boolean) : [];
  const toggle = (id, checked) => {
    const updated = checked
      ? [...selected, String(id)]
      : selected.filter(s => s !== String(id));
    onChange(updated.length > 0 ? updated.join(',') : null);
  };
  return (
    <div style={{
      border: `1px solid ${C.border}`, borderRadius: 8,
      maxHeight: 140, overflowY: 'auto', padding: '6px 8px',
      background: '#fff', display: 'flex', flexDirection: 'column', gap: 4,
    }}>
      {items.length === 0 && (
        <div style={{ fontSize: 12, color: '#9CA3AF' }}>No items found</div>
      )}
      {items.map((item) => {
        const isChecked = selected.includes(String(item[valueKey]));
        return (
          <label key={item[valueKey]} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={isChecked}
              onChange={(e) => toggle(item[valueKey], e.target.checked)} />
            {renderLabel ? renderLabel(item) : item[labelKey]}
          </label>
        );
      })}
    </div>
  );
};

// ─── Upload Button (inline, for a single field) ───────────────────────────────
const UploadImageField = ({ label, value, onChange, containerType, viewTypeId }) => {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const url = await uploadFile({ file });
      onChange(url);
    } catch (err) {
      setError('Upload failed');
      console.error(err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <FormField label={label}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          style={{ ...inputStyle, flex: 1 }}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
          placeholder="https://... or upload →"
        />
        <button
          type="button"
          title="Upload file"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '6px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
            border: `1px solid ${C.border}`, background: uploading ? '#f3f4f6' : '#fff',
            color: C.text, whiteSpace: 'nowrap',
          }}
        >
          <Upload size={13} />
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
        <input ref={inputRef} type="file" accept="image/*,video/mp4" style={{ display: 'none' }} onChange={handleFile} />
      </div>
      {error && <div style={{ fontSize: 11, color: '#EF4444', marginTop: 3 }}>{error}</div>}
      {value && (
        <div style={{ marginTop: 6 }}>
          <MediaThumb url={value} />
        </div>
      )}
    </FormField>
  );
};

const arrowBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28, borderRadius: 6, border: `1px solid ${C.border}`,
  background: '#fff', cursor: 'pointer', fontSize: 11, color: C.text,
};

// ─── Single Payload Item Row (inline editor) ──────────────────────────────────
const PayloadItemRow = ({ item, index, onUpdate, onRemove, onMoveUp, onMoveDown, isFirst, isLast, containerType, viewTypeId, contentCategories }) => {
  const set = (k, v) => onUpdate(item._id, { ...item, [k]: v === '' ? '' : v });

  return (
    <div style={{
      border: `1px solid ${C.border}`, borderRadius: 10, padding: 16,
      background: '#FAFAFA', position: 'relative', marginBottom: 12,
    }}>
      {/* Row header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <GripVertical size={16} color={C.textMuted} />
        <span style={{ fontSize: 12, fontWeight: 700, color: C.textMuted }}>Item #{index + 1}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button type="button" disabled={isFirst} onClick={onMoveUp}
            style={{ ...arrowBtn, opacity: isFirst ? 0.3 : 1 }}>▲</button>
          <button type="button" disabled={isLast} onClick={onMoveDown}
            style={{ ...arrowBtn, opacity: isLast ? 0.3 : 1 }}>▼</button>
          <button type="button" onClick={() => onRemove(item._id)}
            style={{ ...arrowBtn, color: '#EF4444', borderColor: '#FECACA' }}>
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {/* Fields: 2-column grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
        <FormField label="Order Sequence">
          <input type="number" style={inputStyle} value={item.orderSequence}
            onChange={(e) => set('orderSequence', Number(e.target.value))} />
        </FormField>
        <FormField label="Content Category">
          <select style={selectStyle} value={item.contentCategory}
            onChange={(e) => set('contentCategory', e.target.value)}>
            {contentCategories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </FormField>

        <FormField label="Title" col={2}>
          <input style={{ ...inputStyle, gridColumn: 'span 2' }} value={item.title}
            onChange={(e) => set('title', e.target.value)} placeholder="Item title" />
        </FormField>

        <FormField label="Container Type Description" col={2}>
          <input style={inputStyle} value={item.containerTypeDescription}
            onChange={(e) => set('containerTypeDescription', e.target.value)} />
        </FormField>

        <FormField label="Redirect Link" col={2}>
          <input style={inputStyle} value={item.redirectLink}
            onChange={(e) => set('redirectLink', e.target.value)} placeholder="https://..." />
        </FormField>

        {/* Upload fields */}
        <UploadImageField
          label="Hero Image"
          value={item.heroImageUrl}
          onChange={(v) => set('heroImageUrl', v)}
          containerType={containerType}
          viewTypeId={viewTypeId}
        />
        <UploadImageField
          label="Thumbnail"
          value={item.thumbnailUrl}
          onChange={(v) => set('thumbnailUrl', v)}
          containerType={containerType}
          viewTypeId={viewTypeId}
        />
      </div>
    </div>
  );
};

// ─── Payload Builder Section ──────────────────────────────────────────────────
const PayloadBuilder = ({ payload, onChange, containerType, viewTypeId, contentCategories }) => {
  const addItem = () => {
    const newItem = { ...INIT_PAYLOAD_ITEM(), _id: Date.now() + Math.random(), orderSequence: payload.length + 1 };
    onChange([...payload, newItem]);
  };

  const updateItem = (_id, updated) => onChange(payload.map(p => p._id === _id ? updated : p));
  const removeItem = (_id) => onChange(payload.filter(p => p._id !== _id));

  const moveItem = (index, dir) => {
    const arr = [...payload];
    const target = index + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    onChange(arr.map((item, i) => ({ ...item, orderSequence: i + 1 })));
  };

  return (
    <div style={{ marginTop: 4 }}>
      {payload.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '24px 0', color: C.textMuted, fontSize: 13,
          border: `1px dashed ${C.border}`, borderRadius: 8, marginBottom: 12,
        }}>
          No payload items yet. Click "Add Item" to begin.
        </div>
      ) : (
        payload.map((item, index) => (
          <PayloadItemRow
            key={item._id}
            item={item}
            index={index}
            onUpdate={updateItem}
            onRemove={removeItem}
            onMoveUp={() => moveItem(index, -1)}
            onMoveDown={() => moveItem(index, 1)}
            isFirst={index === 0}
            isLast={index === payload.length - 1}
            containerType={containerType}
            viewTypeId={viewTypeId}
            contentCategories={contentCategories}
          />
        ))
      )}
      <button type="button" onClick={addItem}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '7px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
          border: `1px dashed ${C.border}`, background: '#F9FAFB', color: C.text,
          width: '100%', justifyContent: 'center',
        }}>
        <Plus size={15} />
        Add Item
      </button>
    </div>
  );
};

// ─── Payload Item Edit Modal ──────────────────────────────────────────────────
const PayloadItemEditModal = ({ item, index, onSave, onClose, saving, containerType, viewTypeId }) => {
  const [localItem, setLocalItem] = useState({ ...item });
  const set = (k, v) => setLocalItem((prev) => ({ ...prev, [k]: v }));

  return (
    <Modal title={`Edit Payload Item #${index + 1}`} onClose={onClose} wide>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

        <FormField label="Order Sequence">
          <input type="number" style={inputStyle}
            value={localItem.orderSequence ?? ''}
            onChange={(e) => set('orderSequence', Number(e.target.value))} />
        </FormField>

        <FormField label="Content Category">
          <select style={selectStyle}
            value={localItem.contentCategory ?? ''}
            onChange={(e) => set('contentCategory', e.target.value)}>
            {CONTENT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </FormField>

        <FormField label="Title" col={2}>
          <input
            style={{ ...inputStyle, gridColumn: 'span 2' }}
            value={localItem.title ?? ''}
            onChange={(e) => set('title', e.target.value)}
            placeholder="Item title"
            autoFocus
          />
        </FormField>

        <FormField label="Container Type Description" col={2}>
          <input style={inputStyle}
            value={localItem.containerTypeDescription ?? ''}
            onChange={(e) => set('containerTypeDescription', e.target.value)} />
        </FormField>

        <FormField label="Redirect Link" col={2}>
          <input style={inputStyle}
            value={localItem.redirectLink ?? ''}
            onChange={(e) => set('redirectLink', e.target.value)}
            placeholder="https://..." />
        </FormField>

        <UploadImageField
          label="Hero Image"
          value={localItem.heroImageUrl}
          onChange={(v) => set('heroImageUrl', v)}
          containerType={containerType}
          viewTypeId={viewTypeId}
        />
        <UploadImageField
          label="Thumbnail"
          value={localItem.thumbnailUrl}
          onChange={(v) => set('thumbnailUrl', v)}
          containerType={containerType}
          viewTypeId={viewTypeId}
        />
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={() => onSave(localItem)} disabled={saving}>
          {saving ? 'Saving…' : 'Update Item'}
        </Button>
      </div>
    </Modal>
  );
};

// ─── Main Module ──────────────────────────────────────────────────────────────
export function MasterLayoutModule() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [form, setForm] = useState(INIT_FORM());
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState([]);
  const [sortingColumn, setSortingColumn] = useState('orderSequence');
  const [direction, setDirection] = useState('ASC');
  const [containerTypes, setContainerTypes] = useState([]);
  const [viewTypes, setViewTypes] = useState([]);
  const [contentProviders, setContentProviders] = useState([]);
  const [categories, setCategories] = useState([]);

  const [detailPayload, setDetailPayload] = useState([]);
  const [payloadLoading, setPayloadLoading] = useState(false);
  const [contentPage, setContentPage] = useState(0);
  const [contentPageSize] = useState(10);
  const [contentTotal, setContentTotal] = useState(0);
  const [contentTotalPages, setContentTotalPages] = useState(0);
  const [isFetchedContent, setIsFetchedContent] = useState(false);
  const [squenceModal, setSequenceModal] = useState(false);

  // ─── Payload item edit state ─────────────────────────────────────────────
  const [editingPayloadItem, setEditingPayloadItem] = useState(null); // { item, index }
  const [payloadItemSaving, setPayloadItemSaving] = useState(false);

  // ─── Payload mode flag: true when both contentCategory & categoryId are empty
  const isPayloadMode = !form.contentCategory && !form.categoryId;

  // ─── fSet: empty string → null ──────────────────────────────────────────
  const fSet = (k, v) => {
    const val = (v === '' || v === undefined) ? "" : v;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const handleContentCategoryChange = (v) => {
    fSet('contentCategory', v);
    if (v) setForm((f) => ({ ...f, payload: [] }));
  };
  const handleContentType = (v) => {
    fSet('contentType', v);
    if (v) setForm((f) => ({ ...f, payload: [] }));
  };
  const handleCategoryChange = (v) => {
    fSet('categoryId', v);
    if (v) setForm((f) => ({ ...f, payload: [] }));
  };

  // ─── Fetch master dropdowns ──────────────────────────────────────────────
  const fetchMasterData = useCallback(async () => {
    try {
      const [containerRes, viewRes] = await Promise.all([
        apiClient.get('/minusone-service/master/getMasterContainer'),
        apiClient.get('/minusone-service/master/getViewType'),
      ]);
      setContainerTypes(containerRes.data?.responseObject || []);
      setViewTypes(viewRes.data?.responseObject || []);
      let allProviders = [];
      let currentPage = 0;
      const batchSize = 10;
      while (true) {
        const res = await getContentProvider({
          pageNumber: currentPage, pageSize: batchSize,
          sortingColumn: 'id', direction: 'ASC', filters: [],
        });
        if (!res || res?.responseCode === 404 || !res?.responseObject) break;
        const obj = res.responseObject;
        const items = obj?.content || [];
        allProviders = [...allProviders, ...items];
        if (currentPage + 1 >= obj?.totalPages) break;
        currentPage++;
      }
      setContentProviders(allProviders);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchMasterData(); }, [fetchMasterData]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await getCategories({ pageNumber: 0, pageSize: 100, sortingColumn: 'id', direction: 'ASC', filters: [] });
      setCategories(res?.responseObject?.content || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getLayout({ pageNumber: page, pageSize, sortingColumn, direction, filters });
      const obj = res.responseObject;
      setData(obj.content);
      setTotal(obj.totalElements);
      setTotalPages(obj.totalPages);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, pageSize, sortingColumn, direction, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchContent = useCallback(async (row, pg = 0) => {
    setPayloadLoading(true);
    try {
      const res = await getContent({
        pageNumber: pg, pageSize: contentPageSize,
        sortingColumn: 'createdAt', direction: 'DESC',
        filters: [
          { key: 'CONTENT_CATEGORY', value: row.contentCategory },
          { key: 'CATEGORY_IN', value: row.categoryId },
        ],
      });
      const obj = res?.responseObject;
      setDetailPayload(obj?.content || []);
      setContentTotal(obj?.totalElements || 0);
      setContentTotalPages(obj?.totalPages || 0);
      setIsFetchedContent(true);
    } catch (e) {
      console.error(e);
      setDetailPayload([]);
    } finally { setPayloadLoading(false); }
  }, [contentPageSize]);

  const openView = useCallback((row) => {
    setDetailRow(row);
    setContentPage(0);
    setContentTotal(0);
    setContentTotalPages(0);
    setIsFetchedContent(false);
    const hasPayload = Array.isArray(row.payload) && row.payload.length > 0;
    if (hasPayload) {
      setDetailPayload(row.payload);
      setIsFetchedContent(false);
    } else if (row.contentCategory) {
      setDetailPayload([]);
      fetchContent(row, 0);
    } else {
      setDetailPayload([]);
      setIsFetchedContent(false);
    }
  }, [fetchContent]);

  const handleContentPageChange = (pg) => {
    setContentPage(pg);
    fetchContent(detailRow, pg);
  };

  const openAdd = () => { setForm(INIT_FORM()); setModal('add'); };
  const openEdit = (row) => {
    const { createdAt, updatedAt, createdByName, updatedByName, payload, ...rest } = row;
    const payloadWithIds = Array.isArray(rest.payload)
      ? rest.payload.map((p, i) => ({ _id: Date.now() + i, ...p }))
      : [];
    setForm({ ...INIT_FORM(), ...rest });
    setModal(row);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await addOrUpdate({ ...form });
      setModal(null);
      fetchData();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  // ─── Payload item edit handlers ──────────────────────────────────────────
  const openPayloadItemEdit = (item, index) => {
    setEditingPayloadItem({ item: { ...item }, index });
  };

  const handlePayloadItemSave = async (updatedItem) => {
    if (!editingPayloadItem) return;
    setPayloadItemSaving(true);
    try {
      const updatedPayload = detailRow.payload.map((p, i) =>
        i === editingPayloadItem.index ? { ...p, ...updatedItem } : p
      );
      await addOrUpdate({ ...detailRow, payload: updatedPayload });
      // Update detailRow & detailPayload locally so UI reflects change immediately
      const updatedRow = { ...detailRow, payload: updatedPayload };
      setDetailRow(updatedRow);
      setDetailPayload(updatedPayload);
      setEditingPayloadItem(null);
      fetchData(); // background refresh for the main table
    } catch (e) {
      console.error(e);
    } finally {
      setPayloadItemSaving(false);
    }
  };

  const resolveNames = (ids, list, labelKey = 'name') => {
    if (!ids) return '—';
    return String(ids).split(',').map(id => {
      const found = list.find(i => String(i.id) === id.trim());
      return found ? found[labelKey] : id.trim();
    }).join(', ');
  };

  // ─── Payload columns (with edit action) ─────────────────────────────────
  const PAYLOAD_COLUMNS = [
    { key: 'thumbnailUrl', label: 'Thumbnail', render: (v) => <MediaThumb url={v} /> },
    { key: 'orderSequence', label: 'Sequence' },
    { key: 'title', label: 'Title' },
    { key: 'status', label: 'Status', render: (v) => <Badge status={v} /> },
    { key: 'contentCategory', label: 'Category', render: (v) => <CategoryBadge label={v} /> },
    { key: 'createdAt', label: 'Created At', render: (v) => formatDate(v) },
    {
      key: '_editAction',
      label: '',
      render: (_, row, index) => (
        <Button small variant="outline" onClick={() => openPayloadItemEdit(row, index)}>
          <Pencil size={13} strokeWidth={2} />
        </Button>
      ),
    },
  ];

  const columns = [
    { key: 'containerTypeName', label: 'Container Type', viewEnable: true },
    { key: 'containerTypeDescription', label: 'Container Description' },
    { key: 'title', label: 'Title' },
    { key: 'status', label: 'Status', render: (v) => <Badge status={v} /> },
    { key: 'contentCategory', label: 'Content Category', render: (v) => <CategoryBadge label={v} /> },
    { key: 'isAds', label: 'Ads Enable', render: (v) => <BoolBadge val={v} /> },
    { key: 'isMore', label: 'More Enable', render: (v) => <BoolBadge val={v} /> },
    { key: 'updatedAt', label: 'Updated At', render: (val) => formatDate(val) },
    { key: 'createdAt', label: 'Created At', render: (val) => formatDate(val) },
  ];

  return (
    <div>
      <PageHeader title="Master Layout" count={total} onAdd={openAdd}
        actions={
          <Button size="sm" onClick={() => setSequenceModal(true)}
            className="bg-violet-600 hover:bg-violet-700 text-white flex items-center gap-2">
            <ArrowUpDown size={16} strokeWidth={2.5} />
            <span>Reorder</span>
          </Button>
        }
      />
      <div style={cardStyle}>
        <div style={{ display: 'flex', gap: 12, padding: '14px 20px', borderBottom: `1px solid ${C.border}` }}>
          <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(0); }} placeholder="Search layouts…" />
        </div>
        {loading ? <LoadingSpinner /> : (
          <Table columns={columns} data={data}
            onCellClick={(col, row) => col.viewEnable && openView(row)}
            actions={(row) => [
              <Button key="e" small variant="outline" onClick={() => openEdit(row)}>
                <Pencil size={13} strokeWidth={2} />
              </Button>
            ]}
          />
        )}
        <Pagination
          page={page} totalPages={totalPages} totalElements={total} pageSize={pageSize}
          onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(0); }}
        />
      </div>

      {/* ─── Sequence Modal ───────────────────────────────────────────── */}
      {squenceModal && <LayoutSequenceEditor onClose={() => setSequenceModal(false)} />}

      {/* ─── Detail Modal ─────────────────────────────────────────────── */}
      {detailRow && (
        <Modal title={`Detail — ${detailRow.title}`} onClose={() => setDetailRow(null)} wide>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px', fontSize: 13 }}>
            {[
              ['Order Sequence', detailRow.orderSequence],
              ['Container Type', detailRow.containerTypeName],
              ['Container Desc.', detailRow.containerTypeDescription],
              ['Title', detailRow.title],
              ['Status', <Badge status={detailRow.status} />],
              ['Content Category', <CategoryBadge label={detailRow.contentCategory} />],
              ['View Type', detailRow.viewType],
              ['Endpoint URL', detailRow.endpointUrl],
              ['More URL', detailRow.moreUrl],
              ['Ads Type', detailRow.adsType],
              ['Ads Category', detailRow.adsCategory],
              ['Ads ID', detailRow.adsId],
              ['Ads Index', detailRow.adsIndex],
              ['More View', detailRow.moreView],
              ['Category IDs', resolveNames(detailRow.categoryId, categories, 'categoryName')],
              ['Content Providers', resolveNames(detailRow.contentProviderId, contentProviders, 'name')],
              ['Content Limit', detailRow.contentLimit ?? '—'],
              ['Icon Button', detailRow.enableIconButton ? `✅ ${detailRow.iconButtonText || ''}` : '❌'],
              ['Created At', formatDate(detailRow.createdAt)],
              ['Updated At', formatDate(detailRow.updatedAt)],
              ['Created By', detailRow.createdByName],
              ['Updated By', detailRow.updatedByName],
            ].map(([k, v]) => (
              <div key={k} style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: 8 }}>
                <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>{k}</div>
                <div style={{ color: C.text }}>{v ?? '—'}</div>
              </div>
            ))}
          </div>

          {/* Payload / Content Section */}
          <div style={{ marginTop: 24 }}>
            <div style={{
              borderTop: `1px solid ${C.border}`, paddingTop: 16, marginBottom: 12,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: C.text }}>
                {isFetchedContent ? 'Content List' : 'Payload'}
                {isFetchedContent && contentTotal > 0 && (
                  <span style={{ marginLeft: 8, fontWeight: 400, color: C.textMuted, fontSize: 12 }}>
                    ({contentTotal} items)
                  </span>
                )}
                {!isFetchedContent && detailPayload.length > 0 && (
                  <span style={{ marginLeft: 8, fontWeight: 400, color: C.textMuted, fontSize: 12 }}>
                    ({detailPayload.length} items)
                  </span>
                )}
              </p>
              {payloadLoading && <span style={{ fontSize: 12, color: C.textMuted }}>Loading…</span>}
            </div>

            {payloadLoading ? <LoadingSpinner /> : detailPayload.length === 0 ? (
              <div style={{
                textAlign: 'center', padding: '32px 0', color: C.textMuted, fontSize: 13,
                border: `1px dashed ${C.border}`, borderRadius: 8,
              }}>No data available</div>
            ) : (
              <>
                <Table
                  columns={isFetchedContent ? CONTENT_COLUMNS : PAYLOAD_COLUMNS}
                  data={detailPayload}
                />
                {isFetchedContent && contentTotalPages > 1 && (
                  <Pagination
                    page={contentPage} totalPages={contentTotalPages}
                    totalElements={contentTotal} pageSize={contentPageSize}
                    onPageChange={handleContentPageChange} onPageSizeChange={() => { }}
                  />
                )}
              </>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
            <Button variant="ghost" onClick={() => setDetailRow(null)}>Close</Button>
          </div>
        </Modal>
      )}

      {/* ─── Payload Item Edit Modal ──────────────────────────────────── */}
      {editingPayloadItem && (
        <PayloadItemEditModal
          item={editingPayloadItem.item}
          index={editingPayloadItem.index}
          onSave={handlePayloadItemSave}
          onClose={() => setEditingPayloadItem(null)}
          saving={payloadItemSaving}
          containerType={detailRow?.containerType}
          viewTypeId={detailRow?.viewTypeId}
        />
      )}

      {/* ─── Add / Edit Modal ─────────────────────────────────────────── */}
      {modal && (
        <Modal title={modal === 'add' ? 'Add Master Layout' : 'Edit Master Layout'} onClose={() => setModal(null)} wide>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

            <FormField label="Title">
              <input style={inputStyle} value={form.title ?? ''} autoFocus
                onChange={(e) => fSet('title', e.target.value)} placeholder="Layout Block A" />
            </FormField>

            <FormField label="Order Sequence">
              <input type="number" style={inputStyle} value={form.orderSequence ?? ''}
                onChange={(e) => fSet('orderSequence', Number(e.target.value))} />
            </FormField>

            <FormField label="Container Type">
              <select style={inputStyle} value={form.containerType ?? ''}
                onChange={(e) => {
                  const selected = containerTypes.find((c) => c.id === Number(e.target.value));
                  fSet('containerType', Number(e.target.value));
                  fSet('containerTypeDescription', selected?.container || null);
                }}>
                <option value="">-- Select Container --</option>
                {containerTypes.map((c) => (
                  <option key={c.id} value={c.id}>{c.containerName}</option>
                ))}
              </select>
            </FormField>

            <FormField label="Container Type Description">
              <input style={inputStyle} value={form.containerTypeDescription ?? ''}
                onChange={(e) => fSet('containerTypeDescription', e.target.value)} />
            </FormField>

            <FormField label="Endpoint URL" col={2}>
              <input style={inputStyle} value={form.endpointUrl ?? ''}
                onChange={(e) => fSet('endpointUrl', e.target.value)}
                placeholder="https://api.example.com/feed/1" />
            </FormField>

            <FormField label="Hero Image URL">
              <input style={inputStyle} value={form.heroImageUrl ?? ''}
                onChange={(e) => fSet('heroImageUrl', e.target.value)} />
            </FormField>

            <FormField label="Thumbnail URL">
              <input style={inputStyle} value={form.thumbnailUrl ?? ''}
                onChange={(e) => fSet('thumbnailUrl', e.target.value)} />
            </FormField>

            <FormField label="Content Category">
              <select style={selectStyle} value={form.contentCategory ?? ''}
                onChange={(e) => handleContentCategoryChange(e.target.value)}>
                <option value="">-- None (use Payload) --</option>
                {CONTENT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </FormField>

            <FormField label="Content Type">
              <select style={selectStyle} value={form.contentType ?? ''}
                onChange={(e) => handleContentType(e.target.value)}>
                {CONTENT_TYPE.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </FormField>
            <FormField label="Content Shuffle">
              <select style={selectStyle} value={String(form.enableSuffle)}
                onChange={(e) => fSet('enableSuffle', e.target.value === 'true')}>
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </FormField>

            <FormField label="View Type">
              <select style={inputStyle} value={form.viewTypeId ?? ''}
                onChange={(e) => fSet('viewTypeId', Number(e.target.value))}>
                <option value="">-- Select View Type --</option>
                {viewTypes.map((v) => (
                  <option key={v.id} value={v.id}>{v.viewType}</option>
                ))}
              </select>
            </FormField>

            <FormField label="Status">
              <select style={selectStyle} value={form.status ?? 1}
                onChange={(e) => fSet('status', Number(e.target.value))}>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </FormField>

            {/* ── Inline info banner when payload mode is active ─────────── */}
            {isPayloadMode && (
              <div style={{
                gridColumn: 'span 2',
                background: '#EFF6FF', border: '1px solid #BFDBFE',
                borderRadius: 8, padding: '10px 14px',
                fontSize: 12, color: '#1E40AF', display: 'flex', gap: 8, alignItems: 'flex-start',
              }}>
                <span style={{ fontSize: 16 }}>ℹ️</span>
                <span>
                  <strong>Payload mode active</strong> — Content Category aur Category dono empty hain.
                  Neeche manually payload items add karo. "See More" option is mode mein available nahi hai.
                </span>
              </div>
            )}

            {/* ── Ads Configuration ─────────────────────────────────────── */}
            {sectionHeading('Ads Configuration')}

            <FormField label="Is Ads">
              <select style={selectStyle} value={String(form.isAds)}
                onChange={(e) => fSet('isAds', e.target.value === 'true')}>
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </FormField>

            <FormField label="Ads ID">
              <input style={inputStyle} value={form.adsId ?? ''}
                onChange={(e) => fSet('adsId', e.target.value)} />
            </FormField>

            <FormField label="Ads Type">
              <select style={selectStyle} value={form.adsType ?? ''}
                onChange={(e) => fSet('adsType', e.target.value)}>
                {ADS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </FormField>

            <FormField label="Ads Category">
              <select style={selectStyle} value={form.adsCategory ?? ''}
                onChange={(e) => fSet('adsCategory', e.target.value)}>
                {ADS_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </FormField>

            <FormField label="Ads Index">
              <input type="number" style={inputStyle} value={form.adsIndex ?? ''}
                onChange={(e) => fSet('adsIndex', Number(e.target.value))} />
            </FormField>

            {/* ── More / Navigation — hidden in payload mode ─────────────── */}
            {!isPayloadMode && (
              <>
                {sectionHeading('More / Navigation')}
                <FormField label="More URL">
                  <input style={inputStyle} value={form.moreUrl ?? ''}
                    onChange={(e) => fSet('moreUrl', e.target.value)} />
                </FormField>
                <FormField label="More View">
                  <select style={selectStyle} value={form.moreView ?? ''}
                    onChange={(e) => fSet('moreView', e.target.value)}>
                    {MORE_VIEWS.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </FormField>
              </>
            )}

            {/* ── Content Configuration — hidden in payload mode ────────── */}
            {!isPayloadMode && (
              <>
                {sectionHeading('Content Configuration')}

                <FormField label="Content Provider">
                  <MultiCheckList
                    items={contentProviders}
                    value={form.contentProviderId}
                    onChange={(val) => fSet('contentProviderId', val)}
                    labelKey="name"
                    valueKey="id"
                    renderLabel={(item) => (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {item.providerLogo && (
                          <img src={item.providerLogo} alt=""
                            style={{ width: 18, height: 18, borderRadius: '50%', objectFit: 'cover' }}
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        )}
                        {item.name} - {item.category} - {item.contentCategory}
                      </span>
                    )}
                  />
                </FormField>

                <FormField label="Category">
                  <MultiCheckList
                    items={categories}
                    value={form.categoryId}
                    onChange={handleCategoryChange}
                    labelKey="categoryName"
                    valueKey="id"
                  />
                </FormField>

                <FormField label="Content Limit">
                  <input type="number" style={inputStyle} value={form.contentLimit ?? ''}
                    onChange={(e) => fSet('contentLimit', e.target.value === '' ? null : Number(e.target.value))}
                    placeholder="e.g. 10" />
                </FormField>

                <FormField label="Enable Icon Button">
                  <select style={selectStyle} value={String(form.enableIconButton)}
                    onChange={(e) => fSet('enableIconButton', e.target.value === 'true')}>
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </FormField>

                {form.enableIconButton && (
                  <FormField label="Icon Button Text">
                    <input style={inputStyle} value={form.iconButtonText ?? ''}
                      onChange={(e) => fSet('iconButtonText', e.target.value)}
                      placeholder="e.g. Install" />
                  </FormField>
                )}
              </>
            )}

            {/* ─── Payload Builder — shown only in payload mode ──────────── */}
            {isPayloadMode && (
              <>
                {sectionHeading(`Payload Items (${form.payload.length})`)}
                <div style={{ gridColumn: 'span 2' }}>
                  <PayloadBuilder
                    payload={form.payload}
                    onChange={(p) => setForm((f) => ({ ...f, payload: p }))}
                    containerType={form.containerType}
                    viewTypeId={form.viewTypeId}
                    contentCategories={CONTENT_CATEGORIES}
                  />
                </div>
              </>
            )}

          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 28 }}>
            <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : modal === 'add' ? 'Create Layout' : 'Update Layout'}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}